import { Capacitor } from '@capacitor/core';
import { Camera, CameraPermissionState, CameraResultType, CameraSource } from '@capacitor/camera';
import jsQR from 'jsqr';

export const isCapacitorApp = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    const origin = window.location?.origin || '';
    const protocol = window.location?.protocol || '';
    if (protocol === 'capacitor:' || origin.startsWith('capacitor://')) return true;
    return typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};


export const requestCameraPermission = async (): Promise<boolean> => {
  try {
    const res = await Camera.requestPermissions({ permissions: ['camera'] });
    const state = (res as any)?.camera as CameraPermissionState | undefined;
    return state === 'granted' || state === 'limited';
  } catch {
    return false;
  }
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const resp = await fetch(dataUrl);
  return await resp.blob();
};

export const takePhoto = async (): Promise<File> => {
  const photo = await Camera.getPhoto({
    quality: 75,
    allowEditing: false,
    source: CameraSource.Camera,
    resultType: CameraResultType.Uri,
  });

  if (!photo?.webPath) {
    throw new Error('No se pudo obtener la foto');
  }

  const blob = await fetch(photo.webPath).then((r) => r.blob());
  const filename = `photo-${Date.now()}.jpg`;
  return new File([blob], filename, { type: blob.type || 'image/jpeg' });
};

const decodeQrFromImage = async (dataUrl: string): Promise<string | null> => {
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        resolve(code?.data || null);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
};

export const scanQrCodeFromCamera = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  const photo = await Camera.getPhoto({
    quality: 80,
    allowEditing: false,
    source: CameraSource.Camera,
    resultType: CameraResultType.DataUrl,
  });

  if (!photo?.dataUrl) return null;
  return await decodeQrFromImage(photo.dataUrl);
};
